import {
  Building01Icon,
  ChartBarLineIcon,
  CubeIcon,
  DeliveryTruck01Icon,
  GlobeIcon,
  Layers01Icon,
  MapPinIcon,
  Package01Icon,
  RulerIcon,
  Settings02Icon,
  StarIcon,
  UserIcon,
  Building03Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  LocationsSection,
  PartCategoriesSection,
  PartItemsSection,
  PartTypesSection,
  PartUnitsSection,
  SuppliersSection,
  TechnicianCheckoutsSection,
} from '@/components/features/parts/sections';
import { InventoryOptimizationSection } from '@/components/features/parts/sections/InventoryOptimizationSection';
import { PurchaseOrdersSection } from '@/components/features/parts/sections/PurchaseOrdersSection';
import { WarehousesSection } from '@/components/features/parts/sections/WarehousesSection';
import { SupplierScorecardSection } from '@/components/features/parts/sections/SupplierScorecardSection';
import { BomSection } from '@/components/features/parts/sections/BomSection';
import { SitesSection } from '@/components/features/parts/sections/SitesSection';
import { InventoryTransfersSection } from '@/components/features/parts/sections/InventoryTransfersSection';
import { SiteComparisonSection } from '@/components/features/parts/sections/SiteComparisonSection';
import { MobileTabChips } from '@/components/shared/mobile-tab-chips';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';

// Tab-to-group reverse lookup
const TAB_TO_GROUP: Record<string, string> = {
  items: 'inventory',
  checkouts: 'inventory',
  bom: 'inventory',
  suppliers: 'procurement',
  'purchase-orders': 'procurement',
  transfers: 'procurement',
  'part-types': 'configuration',
  categories: 'configuration',
  units: 'configuration',
  locations: 'configuration',
  warehouses: 'configuration',
  sites: 'configuration',
  'supplier-scorecard': 'analytics',
  'site-comparison': 'analytics',
  optimization: 'analytics',
};

const TAB_GROUPS = [
  {
    id: 'inventory',
    labelKey: 'partsManagement.groups.inventory',
    icon: Package01Icon,
    tabs: [
      { value: 'items', labelKey: 'partsManagement.tabs.items', icon: Package01Icon },
      { value: 'checkouts', labelKey: 'partsManagement.tabs.technicianParts', icon: UserIcon },
      { value: 'bom', labelKey: 'partsManagement.tabs.bom', icon: Settings02Icon },
    ],
  },
  {
    id: 'procurement',
    labelKey: 'partsManagement.groups.procurement',
    icon: DeliveryTruck01Icon,
    tabs: [
      { value: 'suppliers', labelKey: 'partsManagement.tabs.suppliers', icon: Building01Icon },
      {
        value: 'purchase-orders',
        labelKey: 'partsManagement.tabs.purchaseOrders',
        icon: DeliveryTruck01Icon,
      },
      { value: 'transfers', labelKey: 'partsManagement.tabs.transfers', icon: DeliveryTruck01Icon },
    ],
  },
  {
    id: 'configuration',
    labelKey: 'partsManagement.groups.configuration',
    icon: Settings02Icon,
    tabs: [
      { value: 'part-types', labelKey: 'partsManagement.tabs.partTypes', icon: CubeIcon },
      { value: 'categories', labelKey: 'partsManagement.tabs.categories', icon: Layers01Icon },
      { value: 'units', labelKey: 'partsManagement.tabs.units', icon: RulerIcon },
      { value: 'locations', labelKey: 'partsManagement.tabs.locations', icon: MapPinIcon },
      { value: 'warehouses', labelKey: 'partsManagement.tabs.warehouses', icon: Building03Icon },
      { value: 'sites', labelKey: 'partsManagement.tabs.sites', icon: GlobeIcon },
    ],
  },
  {
    id: 'analytics',
    labelKey: 'partsManagement.groups.analytics',
    icon: ChartBarLineIcon,
    tabs: [
      {
        value: 'supplier-scorecard',
        labelKey: 'partsManagement.tabs.supplierScorecard',
        icon: StarIcon,
      },
      {
        value: 'site-comparison',
        labelKey: 'partsManagement.tabs.siteComparison',
        icon: ChartBarLineIcon,
      },
      {
        value: 'optimization',
        labelKey: 'partsManagement.tabs.optimization',
        icon: ChartBarLineIcon,
      },
    ],
  },
] as const;

export const Route = createFileRoute('/_authenticated/parts-management/')({
  validateSearch: (search: Record<string, unknown>) => {
    return {
      tab: (search.tab as string) || 'items',
    };
  },
  component: PartsManagementPage,
});

function PartsManagementPage() {
  const navigate = useNavigate();
  const { tab } = useSearch({ from: '/_authenticated/parts-management/' });
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const activeGroupId = TAB_TO_GROUP[tab] || 'inventory';
  const activeGroupDef = TAB_GROUPS.find((g) => g.id === activeGroupId)!;

  const handleTabChange = (value: string) => {
    navigate({
      to: '/parts-management',
      search: { tab: value } as any,
    });
  };

  const handleGroupChange = (groupId: string) => {
    if (groupId === activeGroupId) return;
    const group = TAB_GROUPS.find((g) => g.id === groupId);
    if (group && group.tabs.length > 0) {
      handleTabChange(group.tabs[0].value);
    }
  };

  const renderContent = () => (
    <>
      {tab === 'items' && <PartItemsSection />}
      {tab === 'part-types' && <PartTypesSection />}
      {tab === 'categories' && <PartCategoriesSection />}
      {tab === 'units' && <PartUnitsSection />}
      {tab === 'suppliers' && <SuppliersSection />}
      {tab === 'locations' && <LocationsSection />}
      {tab === 'checkouts' && <TechnicianCheckoutsSection />}
      {tab === 'purchase-orders' && <PurchaseOrdersSection />}
      {tab === 'warehouses' && <WarehousesSection />}
      {tab === 'supplier-scorecard' && <SupplierScorecardSection />}
      {tab === 'bom' && <BomSection />}
      {tab === 'sites' && <SitesSection />}
      {tab === 'transfers' && <InventoryTransfersSection />}
      {tab === 'site-comparison' && <SiteComparisonSection />}
      {tab === 'optimization' && <InventoryOptimizationSection />}
    </>
  );

  if (isMobile) {
    return (
      <div className="flex flex-col gap-4">
        <div>
          <h1 className="text-xl font-bold tracking-tight">{t('partsManagement.title')}</h1>
          <p className="text-xs text-muted-foreground">{t('partsManagement.description')}</p>
        </div>

        <MobileTabChips
          tabs={TAB_GROUPS.map((g) => ({ value: g.id, label: t(g.labelKey) }))}
          value={activeGroupId}
          onChange={handleGroupChange}
        />

        <MobileTabChips
          tabs={activeGroupDef.tabs.map((sub) => ({ value: sub.value, label: t(sub.labelKey) }))}
          value={tab}
          onChange={handleTabChange}
        />

        {renderContent()}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 sm:gap-6">
      <div>
        <h1 className="text-xl sm:text-2xl font-bold tracking-tight">
          {t('partsManagement.title')}
        </h1>
        <p className="text-xs sm:text-sm text-muted-foreground">
          {t('partsManagement.description')}
        </p>
      </div>

      {/* Group bar */}
      <div className="bg-muted inline-flex h-9 w-full items-center justify-center rounded-lg p-[3px]">
        {TAB_GROUPS.map((group) => (
          <button
            key={group.id}
            onClick={() => handleGroupChange(group.id)}
            className={cn(
              'inline-flex h-[calc(100%-1px)] flex-1 items-center justify-center gap-1.5 rounded-md px-2 py-1 text-sm font-medium transition-colors',
              activeGroupId === group.id
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <HugeiconsIcon icon={group.icon} className="h-4 w-4" />
            <span>{t(group.labelKey)}</span>
          </button>
        ))}
      </div>

      {/* Sub-tabs + content */}
      <Tabs value={tab} onValueChange={handleTabChange} className="w-full">
        <TabsList className="w-fit">
          {activeGroupDef.tabs.map((sub) => (
            <TabsTrigger key={sub.value} value={sub.value} className="text-xs sm:text-sm">
              <div className="flex items-center gap-1 sm:gap-2">
                <HugeiconsIcon icon={sub.icon} className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                <span>{t(sub.labelKey)}</span>
              </div>
            </TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value={tab} className="mt-4">
          {renderContent()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
